/**
 * JJFO delivery rules engine — theme port.
 *
 * PORTED FROM, and kept deliberately close to, the checkout UI extension's
 * engine so the product page and the checkout answer the same question the same
 * way:
 *
 *   JJFO-Delivery-Date-Picker/extensions/delivery-date-picker/src/rules-engine/
 *     dateUtils.js, postcodeRegions.js, localDistricts.js, localRadius.js,
 *     bankHolidays.js, rulesEngine.js, configSchema.js
 *
 *   Copied: 1 September 2026.
 *
 *   14 October 2026: CONFIG.regionDays and six POSTCODE_AREA_TABLE rows
 *   (TQ, SA, TN, CT, LA, CA -> proovia_only) changed to mirror what RB saved
 *   in the checkout app's dashboard that morning, so both calendars agree.
 *
 * The sections below mirror those files one-for-one, in dependency order, so
 * re-syncing after a change upstream is a per-section copy-paste rather than a
 * re-read of the whole thing. That repo's `selfTest.js` remains the source of
 * truth for what this is supposed to produce — it is not duplicated here.
 *
 * TWO deliberate differences from the extension:
 *
 * 1. Classic script, not ES modules. This theme has no module infrastructure
 *    (no `type="module"`, no import/export in any other asset), so the whole
 *    engine is one IIFE exposing a single global rather than a module graph.
 *
 * 2. The merchant dashboard's published rules are NOT read. The product page
 *    runs on the bundled defaults only, by product decision. That removes the
 *    entire reason configSchema.js's reader/validator layer existed — there is
 *    no untrusted document to validate — so only the DEFAULT_CONFIG *values*
 *    are carried over, written directly in the engine-ready shape that file's
 *    `toResolved()` produced (cut-offs parsed, lists indexed as Sets).
 */
(function () {
  'use strict';

  // ===========================================================================
  // dateUtils.js
  //
  // Pure calendar-date primitives. No bank-holiday knowledge lives here.
  // Dates are ISO strings (YYYY-MM-DD) everywhere in the engine, never JS Date
  // objects, so there is no ambiguity about which timezone a date "belongs to".
  // ===========================================================================

  var UK_TIME_ZONE = 'Europe/London';

  var UK_DATE_FORMAT = new Intl.DateTimeFormat('en-CA', {
    timeZone: UK_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  var UK_TIME_FORMAT = new Intl.DateTimeFormat('en-GB', {
    timeZone: UK_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

  /**
   * Splits a JS Date into its UK wall-clock calendar date and time-of-day,
   * independent of the shopper's own timezone. Cut-off times are always
   * evaluated in UK time regardless of where the shopper is browsing from.
   */
  function getUkDateTime(date) {
    var isoDate = UK_DATE_FORMAT.format(date);
    var parts = UK_TIME_FORMAT.formatToParts(date);
    var hour = Number(
      parts.find(function (p) {
        return p.type === 'hour';
      }).value
    );
    var minute = Number(
      parts.find(function (p) {
        return p.type === 'minute';
      }).value
    );
    return { isoDate: isoDate, minutesSinceMidnight: hour * 60 + minute };
  }

  /**
   * Adds (or subtracts) calendar days to an ISO date string. Field arithmetic
   * on the calendar, NOT elapsed time — done via UTC field construction so a
   * DST transition never shifts the result by an hour.
   */
  function addDays(isoDate, days) {
    var parts = isoDate.split('-').map(Number);
    var shifted = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + days));
    return isoFromUtcFields(shifted);
  }

  function isoFromUtcFields(utcDate) {
    var year = utcDate.getUTCFullYear();
    var month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
    var day = String(utcDate.getUTCDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  /** 0 = Sunday ... 6 = Saturday, matching JS Date#getDay(). */
  function dayOfWeek(isoDate) {
    var parts = isoDate.split('-').map(Number);
    return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2])).getUTCDay();
  }

  /** Lexicographic compare works directly on YYYY-MM-DD strings. */
  function compareIsoDates(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
  }

  // ===========================================================================
  // postcodeRegions.js
  //
  // Generated from jjfo-postcode-lookup.xlsx ("Postcode Lookup" tab) — do not
  // hand-edit. `confidence` and `notes` are informational only; they do not
  // affect runtime routing. They are kept so this table stays a straight copy
  // of the extension's, diffable line by line.
  // ===========================================================================

  /**
   * Postcode AREA (the letters before the digits, e.g. "SK" in "SK1 1RJ")
   * -> JJFO region. `proovia_only` means JJFO's own van does not serve the area.
   */
  var POSTCODE_AREA_TABLE = {
    AB: { region: 'proovia_only', town: 'Aberdeen', confidence: 'High', notes: null },
    DD: { region: 'proovia_only', town: 'Dundee', confidence: 'High', notes: null },
    DG: { region: 'proovia_only', town: 'Dumfries', confidence: 'High', notes: null },
    EH: { region: 'proovia_only', town: 'Edinburgh', confidence: 'High', notes: null },
    FK: { region: 'proovia_only', town: 'Falkirk', confidence: 'High', notes: null },
    G: { region: 'proovia_only', town: 'Glasgow', confidence: 'High', notes: null },
    HS: { region: 'proovia_only', town: 'Outer Hebrides', confidence: 'Medium', notes: 'Island - Proovia (placeholder carrier)' },
    IV: { region: 'proovia_only', town: 'Inverness', confidence: 'High', notes: null },
    KA: { region: 'proovia_only', town: 'Kilmarnock', confidence: 'High', notes: null },
    KW: { region: 'proovia_only', town: 'Kirkwall/Orkney', confidence: 'Medium', notes: 'Island - Proovia (placeholder carrier)' },
    KY: { region: 'proovia_only', town: 'Kirkcaldy', confidence: 'High', notes: null },
    ML: { region: 'proovia_only', town: 'Motherwell', confidence: 'High', notes: null },
    PA: { region: 'proovia_only', town: 'Paisley', confidence: 'High', notes: null },
    PH: { region: 'proovia_only', town: 'Perth', confidence: 'High', notes: null },
    TD: { region: 'proovia_only', town: 'Galashiels', confidence: 'High', notes: null },
    ZE: { region: 'proovia_only', town: 'Shetland', confidence: 'Medium', notes: 'Island - Proovia (placeholder carrier)' },
    EX: { region: 'proovia_only', town: 'Exeter', confidence: 'High', notes: "Excluded from Wales & SW Friday route; JJFO van doesn't serve" },
    PL: { region: 'proovia_only', town: 'Plymouth', confidence: 'High', notes: null },
    TR: { region: 'proovia_only', town: 'Truro', confidence: 'High', notes: null },
    M: { region: 'northwest', town: 'Manchester', confidence: 'High', notes: null },
    L: { region: 'northwest', town: 'Liverpool', confidence: 'High', notes: null },
    WA: { region: 'northwest', town: 'Warrington', confidence: 'High', notes: null },
    SK: { region: 'northwest', town: 'Stockport', confidence: 'High', notes: 'JJFO base near here (SK1 1RJ)' },
    BB: { region: 'northwest', town: 'Blackburn', confidence: 'High', notes: null },
    BL: { region: 'northwest', town: 'Bolton', confidence: 'High', notes: null },
    FY: { region: 'northwest', town: 'Blackpool', confidence: 'High', notes: null },
    LA: { region: 'proovia_only', town: 'Lancaster', confidence: 'High', notes: 'Set to Proovia only in the checkout app, 14 Oct 2026' },
    OL: { region: 'northwest', town: 'Oldham', confidence: 'High', notes: null },
    PR: { region: 'northwest', town: 'Preston', confidence: 'High', notes: null },
    WN: { region: 'northwest', town: 'Wigan', confidence: 'High', notes: null },
    CA: { region: 'proovia_only', town: 'Carlisle', confidence: 'High', notes: 'Set to Proovia only in the checkout app, 14 Oct 2026' },
    CH: { region: 'northwest', town: 'Chester', confidence: 'Medium', notes: 'Cheshire/Merseyside - verify' },
    CW: { region: 'northwest', town: 'Crewe', confidence: 'Medium', notes: 'Cheshire - NW/Midlands boundary; verify' },
    NE: { region: 'north', town: 'Newcastle', confidence: 'High', notes: null },
    SR: { region: 'north', town: 'Sunderland', confidence: 'High', notes: null },
    DH: { region: 'north', town: 'Durham', confidence: 'High', notes: null },
    DL: { region: 'north', town: 'Darlington', confidence: 'High', notes: null },
    TS: { region: 'north', town: 'Teesside', confidence: 'High', notes: null },
    YO: { region: 'north', town: 'York', confidence: 'Medium', notes: 'Yorkshire - North/Midlands boundary; verify' },
    HG: { region: 'north', town: 'Harrogate', confidence: 'Medium', notes: 'Yorkshire - verify' },
    HU: { region: 'north', town: 'Hull', confidence: 'Medium', notes: 'Yorkshire - verify' },
    LS: { region: 'north', town: 'Leeds', confidence: 'Low', notes: 'Yorkshire - North or Midlands? verify' },
    BD: { region: 'north', town: 'Bradford', confidence: 'Low', notes: 'Yorkshire - verify' },
    HX: { region: 'north', town: 'Halifax', confidence: 'Low', notes: 'Yorkshire - verify' },
    HD: { region: 'north', town: 'Huddersfield', confidence: 'Low', notes: 'Yorkshire - verify' },
    WF: { region: 'north', town: 'Wakefield', confidence: 'Low', notes: 'Yorkshire - verify' },
    S: { region: 'north', town: 'Sheffield', confidence: 'Low', notes: 'S.Yorks - North or Midlands? verify' },
    DN: { region: 'north', town: 'Doncaster', confidence: 'Low', notes: 'S.Yorks - verify' },
    B: { region: 'midlands', town: 'Birmingham', confidence: 'High', notes: null },
    CV: { region: 'midlands', town: 'Coventry', confidence: 'High', notes: null },
    DY: { region: 'midlands', town: 'Dudley', confidence: 'High', notes: null },
    WV: { region: 'midlands', town: 'Wolverhampton', confidence: 'High', notes: null },
    WS: { region: 'midlands', town: 'Walsall', confidence: 'High', notes: null },
    ST: { region: 'midlands', town: 'Stoke-on-Trent', confidence: 'Medium', notes: 'NW/Midlands boundary - verify' },
    TF: { region: 'midlands', town: 'Telford', confidence: 'High', notes: null },
    DE: { region: 'east', town: 'Derby', confidence: 'High', notes: null },
    NG: { region: 'east', town: 'Nottingham', confidence: 'High', notes: null },
    LE: { region: 'east', town: 'Leicester', confidence: 'High', notes: null },
    NN: { region: 'east', town: 'Northampton', confidence: 'High', notes: 'Client: East Midlands, Tuesdays only' },
    WR: { region: 'midlands', town: 'Worcester', confidence: 'Medium', notes: 'Midlands/Wales&SW boundary - verify' },
    HR: { region: 'midlands', town: 'Hereford', confidence: 'Low', notes: 'Midlands or Wales&SW? verify' },
    SY: { region: 'midlands', town: 'Shrewsbury', confidence: 'Low', notes: 'Shropshire/Mid-Wales - verify' },
    LN: { region: 'east', town: 'Lincoln', confidence: 'High', notes: 'Client: East Midlands, Tuesdays only' },
    PE: { region: 'east', town: 'Peterborough', confidence: 'High', notes: "Client: East Midlands. PE30-38 is west Norfolk (King's Lynn), also Tuesdays" },
    NR: { region: 'east', town: 'Norwich', confidence: 'High', notes: 'Client: Norwich/Norfolk, Tuesdays only' },
    IP: { region: 'east', town: 'Ipswich', confidence: 'Medium', notes: 'Moved with Norfolk: IP20-27 (Diss, Thetford, Brandon) is Norfolk and an area cannot be split, so Suffolk follows. Confirm this is intended' },
    CB: { region: 'east', town: 'Cambridge', confidence: 'High', notes: 'Client: Cambridge, Tuesdays only' },
    MK: { region: 'south_east', town: 'Milton Keynes', confidence: 'Low', notes: 'Midlands/SE boundary - verify' },
    E: { region: 'south_east', town: 'London E', confidence: 'High', notes: null },
    EC: { region: 'south_east', town: 'London EC', confidence: 'High', notes: null },
    N: { region: 'south_east', town: 'London N', confidence: 'High', notes: null },
    NW: { region: 'south_east', town: 'London NW', confidence: 'High', notes: null },
    SE: { region: 'south_east', town: 'London SE', confidence: 'High', notes: null },
    SW: { region: 'south_east', town: 'London SW', confidence: 'High', notes: null },
    W: { region: 'south_east', town: 'London W', confidence: 'High', notes: null },
    WC: { region: 'south_east', town: 'London WC', confidence: 'High', notes: null },
    BR: { region: 'south_east', town: 'Bromley', confidence: 'High', notes: null },
    CR: { region: 'south_east', town: 'Croydon', confidence: 'High', notes: null },
    DA: { region: 'south_east', town: 'Dartford', confidence: 'High', notes: null },
    EN: { region: 'south_east', town: 'Enfield', confidence: 'High', notes: null },
    HA: { region: 'south_east', town: 'Harrow', confidence: 'High', notes: null },
    IG: { region: 'south_east', town: 'Ilford', confidence: 'High', notes: null },
    KT: { region: 'south_east', town: 'Kingston', confidence: 'High', notes: null },
    RM: { region: 'south_east', town: 'Romford', confidence: 'High', notes: null },
    SM: { region: 'south_east', town: 'Sutton', confidence: 'High', notes: null },
    TW: { region: 'south_east', town: 'Twickenham', confidence: 'High', notes: null },
    UB: { region: 'south_east', town: 'Uxbridge', confidence: 'High', notes: null },
    WD: { region: 'south_east', town: 'Watford', confidence: 'High', notes: null },
    AL: { region: 'south_east', town: 'St Albans', confidence: 'High', notes: null },
    SG: { region: 'south_east', town: 'Stevenage', confidence: 'Medium', notes: 'Herts - verify' },
    LU: { region: 'south_east', town: 'Luton', confidence: 'Medium', notes: 'Beds - verify' },
    HP: { region: 'south_east', town: 'Hemel Hempstead', confidence: 'Medium', notes: 'verify' },
    CM: { region: 'south_east', town: 'Chelmsford', confidence: 'High', notes: null },
    CO: { region: 'south_east', town: 'Colchester', confidence: 'Medium', notes: 'Essex - verify' },
    SS: { region: 'south_east', town: 'Southend', confidence: 'High', notes: null },
    GU: { region: 'south_east', town: 'Guildford', confidence: 'High', notes: null },
    RG: { region: 'south_east', town: 'Reading', confidence: 'Medium', notes: 'Berks - SE/SW boundary; verify' },
    SL: { region: 'south_east', town: 'Slough', confidence: 'High', notes: null },
    OX: { region: 'south_east', town: 'Oxford', confidence: 'Low', notes: 'Oxon - SE or Midlands? verify' },
    ME: { region: 'south_east', town: 'Maidstone', confidence: 'High', notes: null },
    CT: { region: 'proovia_only', town: 'Canterbury', confidence: 'High', notes: 'Set to Proovia only in the checkout app, 14 Oct 2026' },
    TN: { region: 'proovia_only', town: 'Tonbridge', confidence: 'High', notes: 'Set to Proovia only in the checkout app, 14 Oct 2026' },
    RH: { region: 'south_east', town: 'Redhill', confidence: 'High', notes: null },
    BN: { region: 'south_east', town: 'Brighton', confidence: 'High', notes: null },
    PO: { region: 'south_east', town: 'Portsmouth', confidence: 'Low', notes: 'Hants - SE or SW? verify' },
    SO: { region: 'south_east', town: 'Southampton', confidence: 'Low', notes: 'Hants - SE or SW? verify' },
    CF: { region: 'wales_south_west', town: 'Cardiff', confidence: 'High', notes: null },
    NP: { region: 'wales_south_west', town: 'Newport', confidence: 'High', notes: null },
    SA: { region: 'proovia_only', town: 'Swansea', confidence: 'High', notes: 'Set to Proovia only in the checkout app, 14 Oct 2026' },
    LD: { region: 'wales_south_west', town: 'Llandrindod Wells', confidence: 'High', notes: null },
    LL: { region: 'wales_south_west', town: 'Llandudno', confidence: 'Medium', notes: 'N.Wales - verify vs NW' },
    BS: { region: 'wales_south_west', town: 'Bristol', confidence: 'High', notes: null },
    BA: { region: 'wales_south_west', town: 'Bath', confidence: 'High', notes: null },
    GL: { region: 'wales_south_west', town: 'Gloucester', confidence: 'Medium', notes: 'SW/Midlands boundary - verify' },
    TA: { region: 'wales_south_west', town: 'Taunton', confidence: 'High', notes: null },
    DT: { region: 'wales_south_west', town: 'Dorchester', confidence: 'Medium', notes: 'Dorset - verify' },
    BH: { region: 'wales_south_west', town: 'Bournemouth', confidence: 'Low', notes: 'Dorset - SW or SE? verify' },
    SP: { region: 'wales_south_west', town: 'Salisbury', confidence: 'Medium', notes: 'Wilts - verify' },
    SN: { region: 'wales_south_west', town: 'Swindon', confidence: 'Low', notes: 'Wilts - SW or SE? verify' },
    TQ: { region: 'proovia_only', town: 'Torquay', confidence: 'High', notes: 'Set to Proovia only in the checkout app, 14 Oct 2026' },
    BT: { region: 'proovia_only', town: 'Belfast (N. Ireland)', confidence: 'Medium', notes: 'Proovia = placeholder for actual carrier (delivery confirmed)' },
    IM: { region: 'proovia_only', town: 'Isle of Man', confidence: 'Medium', notes: 'Proovia = placeholder for actual carrier (delivery confirmed)' },
    GY: { region: 'proovia_only', town: 'Guernsey', confidence: 'Medium', notes: 'Proovia = placeholder for actual carrier (delivery confirmed)' },
    JE: { region: 'proovia_only', town: 'Jersey', confidence: 'Medium', notes: 'Proovia = placeholder for actual carrier (delivery confirmed)' },
  };

  // Matched against the whitespace-stripped postcode, so "SK11RJ" and "SK1 1RJ"
  // both parse the same way regardless of how the shopper typed it.
  var POSTCODE_PATTERN = /^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})$/;

  /**
   * Parses a raw postcode into its normalized form, outward code (e.g. "SK1")
   * and area (e.g. "SK"). Returns null for anything that doesn't match the UK
   * postcode shape — callers treat that as "unrecognised postcode", not an error.
   */
  function parsePostcode(raw) {
    var compact = String(raw == null ? '' : raw)
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '');
    var match = compact.match(POSTCODE_PATTERN);
    if (!match) return null;
    var outward = match[1];
    var inward = match[2];
    var area = outward.match(/^[A-Z]{1,2}/)[0];
    return { normalized: outward + ' ' + inward, outward: outward, inward: inward, area: area };
  }

  /**
   * Resolves a postcode to its JJFO region, or null if it can't be parsed or
   * its area isn't in the table — both mean "unrecognised postcode" to the rest
   * of the engine.
   */
  function resolveRegion(rawPostcode, postcodeAreas) {
    var areas = postcodeAreas || POSTCODE_AREA_TABLE;
    var parsed = parsePostcode(rawPostcode);
    if (!parsed) return null;
    var entry = areas[parsed.area];
    return entry ? entry.region : null;
  }

  // ===========================================================================
  // localDistricts.js
  //
  // GENERATED DATA — do not hand-edit. Every UK postcode DISTRICT (outward
  // code) treated as "local": within 50 miles of SK1 1RJ, computed at
  // postcode/district level. 457 districts across 27 postcode areas, 14 of them
  // outside the Northwest region — local is defined by distance alone, not by
  // region. See the upstream file for how the list was built and which 16
  // boundary-straddling districts were deliberately excluded to avoid
  // over-promising a next-day date JJFO cannot actually hit.
  // ===========================================================================

  var LOCAL_DISTRICTS = new Set([
    'BB1', 'BB2', 'BB3', 'BB4', 'BB5', 'BB6', 'BB7', 'BB8', 'BB9', 'BB10', 'BB11', 'BB12', 'BB18',
    'BD1', 'BD2', 'BD3', 'BD4', 'BD5', 'BD6', 'BD7', 'BD8', 'BD9', 'BD10', 'BD11', 'BD12', 'BD13',
    'BD14', 'BD15', 'BD16', 'BD17', 'BD18', 'BD19', 'BD20', 'BD21', 'BD22', 'BD23', 'BD97',
    'BD98', 'BD99',
    'BL0', 'BL1', 'BL2', 'BL3', 'BL4', 'BL5', 'BL6', 'BL7', 'BL8', 'BL9', 'BL11', 'BL78',
    'CH1', 'CH2', 'CH3', 'CH4', 'CH5', 'CH6', 'CH7', 'CH8', 'CH25', 'CH26', 'CH27', 'CH28',
    'CH29', 'CH30', 'CH31', 'CH32', 'CH33', 'CH34', 'CH41', 'CH42', 'CH43', 'CH44', 'CH45',
    'CH46', 'CH47', 'CH48', 'CH49', 'CH60', 'CH61', 'CH62', 'CH63', 'CH64', 'CH65', 'CH66',
    'CH70', 'CH88', 'CH99',
    'CW1', 'CW2', 'CW3', 'CW4', 'CW5', 'CW6', 'CW7', 'CW8', 'CW9', 'CW10', 'CW11', 'CW12', 'CW98',
    'DE1', 'DE3', 'DE4', 'DE5', 'DE6', 'DE7', 'DE14', 'DE15', 'DE21', 'DE22', 'DE23', 'DE24',
    'DE45', 'DE55', 'DE56', 'DE65', 'DE75', 'DE99',
    'DN1', 'DN2', 'DN3', 'DN4', 'DN5', 'DN6', 'DN11', 'DN12',
    'FY1', 'FY2', 'FY3', 'FY4', 'FY5', 'FY6', 'FY8',
    'HD1', 'HD2', 'HD3', 'HD4', 'HD5', 'HD6', 'HD7', 'HD8', 'HD9',
    'HG1', 'HG2',
    'HX1', 'HX2', 'HX3', 'HX4', 'HX5', 'HX6', 'HX7',
    'L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7', 'L8', 'L9', 'L10', 'L11', 'L12', 'L13', 'L14',
    'L15', 'L16', 'L17', 'L18', 'L19', 'L20', 'L21', 'L22', 'L23', 'L24', 'L25', 'L26', 'L27',
    'L28', 'L29', 'L30', 'L31', 'L32', 'L33', 'L34', 'L35', 'L36', 'L37', 'L38', 'L39', 'L40',
    'L67', 'L68', 'L69', 'L70', 'L71', 'L72', 'L74', 'L75',
    'LL11', 'LL12', 'LL13', 'LL14',
    'LS1', 'LS2', 'LS3', 'LS4', 'LS5', 'LS6', 'LS7', 'LS8', 'LS9', 'LS10', 'LS11', 'LS12', 'LS13',
    'LS14', 'LS15', 'LS16', 'LS17', 'LS18', 'LS19', 'LS20', 'LS21', 'LS23', 'LS25', 'LS26',
    'LS27', 'LS28', 'LS29', 'LS88', 'LS98', 'LS99',
    'M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9', 'M11', 'M12', 'M13', 'M14', 'M15',
    'M16', 'M17', 'M18', 'M19', 'M20', 'M21', 'M22', 'M23', 'M24', 'M25', 'M26', 'M27', 'M28',
    'M29', 'M30', 'M31', 'M32', 'M33', 'M34', 'M35', 'M38', 'M40', 'M41', 'M43', 'M44', 'M45',
    'M46', 'M50', 'M60', 'M61', 'M90', 'M99',
    'NG15', 'NG16', 'NG17', 'NG18', 'NG19', 'NG20', 'NG21',
    'OL1', 'OL2', 'OL3', 'OL4', 'OL5', 'OL6', 'OL7', 'OL8', 'OL9', 'OL10', 'OL11', 'OL12', 'OL13',
    'OL14', 'OL15', 'OL16',
    'PR0', 'PR1', 'PR2', 'PR3', 'PR4', 'PR5', 'PR6', 'PR7', 'PR8', 'PR9', 'PR11', 'PR25', 'PR26',
    'S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8', 'S9', 'S10', 'S11', 'S12', 'S13', 'S14',
    'S17', 'S18', 'S20', 'S21', 'S25', 'S26', 'S32', 'S33', 'S35', 'S36', 'S40', 'S41', 'S42',
    'S43', 'S44', 'S45', 'S49', 'S60', 'S61', 'S62', 'S63', 'S64', 'S65', 'S66', 'S70', 'S71',
    'S72', 'S73', 'S74', 'S75', 'S80', 'S81', 'S95', 'S96', 'S97', 'S98', 'S99',
    'SK1', 'SK2', 'SK3', 'SK4', 'SK5', 'SK6', 'SK7', 'SK8', 'SK9', 'SK10', 'SK11', 'SK12', 'SK13',
    'SK14', 'SK15', 'SK16', 'SK17', 'SK22', 'SK23',
    'ST1', 'ST2', 'ST3', 'ST4', 'ST5', 'ST6', 'ST7', 'ST8', 'ST9', 'ST10', 'ST11', 'ST12', 'ST13',
    'ST14', 'ST15', 'ST16', 'ST17', 'ST18', 'ST20', 'ST21', 'ST55',
    'SY12', 'SY13', 'SY14',
    'TF9', 'TF10',
    'WA1', 'WA2', 'WA3', 'WA4', 'WA5', 'WA6', 'WA7', 'WA8', 'WA9', 'WA10', 'WA11', 'WA12', 'WA13',
    'WA14', 'WA15', 'WA16', 'WA55', 'WA88',
    'WF1', 'WF2', 'WF3', 'WF4', 'WF5', 'WF6', 'WF7', 'WF8', 'WF9', 'WF10', 'WF11', 'WF12', 'WF13',
    'WF14', 'WF15', 'WF16', 'WF17', 'WF90',
    'WN1', 'WN2', 'WN3', 'WN4', 'WN5', 'WN6', 'WN7', 'WN8',
    'WS15',
  ]);

  // ===========================================================================
  // configSchema.js — the bundled defaults ONLY, pre-resolved.
  //
  // This is DEFAULT_CONFIG written directly in the shape `toResolved()`
  // produced: cut-offs already parsed to {hour, minute}, blackout and district
  // lists already indexed as Sets. The reader/validator layer around it exists
  // upstream to defend against an untrusted merchant-published document; the
  // product page never reads one, so none of it is carried over.
  // ===========================================================================

  var CONFIG = {
    // JJFO's own-courier delivery days per region, as JS Date#getDay() values:
    // 0 = Sunday ... 6 = Saturday.
    regionDays: {
      // Mirrors the checkout app's "Delivery days" screen as saved by RB on
      // 14 October 2026 (10:27 UK). Keep the two in step: if the app changes,
      // change this too.
      northwest: [0, 1, 2, 3, 4, 5], // every day except Saturday
      north: [2], // Tuesday
      midlands: [1, 3, 5], // Mon, Wed, Fri (app box "West Midlands")
      east: [3, 5], // Wed, Fri (app box "East Midlands & East Anglia")
      south_east: [1, 3], // Mon, Wed
      wales_south_west: [3, 4], // Wed, Thu (app box "Wales & South West")
      proovia_only: [],
    },

    // UK wall-clock times. `null` means no cut-off — an order counts as placed
    // today whatever time it arrives. DPD is null by client confirmation.
    cutoffs: {
      jjfoLocal: { hour: 17, minute: 0 },
      jjfoNonLocal: { hour: 12, minute: 0 },
      proovia: { hour: 12, minute: 0 },
      dpd: null,
    },

    // Dates nothing can be delivered on. Bank holidays are computed rather than
    // listed (see below), so they need no entry here. `added`/`removed` are the
    // merchant's overrides upstream and are always empty in this port.
    blackouts: {
      christmasDays: new Set(['12-25', '12-26']), // MM-DD, whatever weekday they land on
      added: new Set(),
      removed: new Set(),
    },

    bookingWindowDays: 90,

    local: {
      basePostcode: 'SK1 1RJ',
      radiusMiles: 50,
      districts: LOCAL_DISTRICTS,
    },

    storageMonths: 9,

    // Shown under the chosen date and saved to the order, so these are the words
    // the customer agreed to and the warehouse reads back.
    serviceLabels: {
      jjfo: 'Full service — 2-man delivery, room of choice, unpacking, assembly and packaging taken away.',
      proovia: '2-man delivery to your room of choice.',
      dpd: 'Standard delivery',
    },

    postcodeAreas: POSTCODE_AREA_TABLE,
  };

  // ===========================================================================
  // bankHolidays.js
  //
  // UK bank holidays for England & Wales (JJFO packs in Stockport). COMPUTED
  // for any year rather than listed, because every one of the eight standard
  // holidays is fully determined by the calendar — a hardcoded table silently
  // corrupts both availability and lead times once the booking window runs past
  // its last entry.
  //
  // What a formula cannot predict is a one-off holiday announced by government
  // (Platinum Jubilee, State Funeral, Coronation — three in four years). Those
  // are merchant-entered upstream via `blackouts.added`, which is always empty
  // here: a one-off closure would have to be added to CONFIG by hand.
  // ===========================================================================

  /** Easter Sunday via the anonymous Gregorian computus. Exact for any year. */
  function easterSunday(year) {
    var a = year % 19;
    var b = Math.floor(year / 100);
    var c = year % 100;
    var d = Math.floor(b / 4);
    var e = b % 4;
    var f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4);
    var k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31);
    var day = ((h + l - 7 * m + 114) % 31) + 1;
    return ymdToIso(year, month, day);
  }

  function ymdToIso(year, month, day) {
    return year + '-' + String(month).padStart(2, '0') + '-' + String(day).padStart(2, '0');
  }

  function isWeekend(iso) {
    var dow = dayOfWeek(iso);
    return dow === 0 || dow === 6;
  }

  /** The nth Monday of a month, e.g. nthMondayOf(2026, 5, 1) = early May. */
  function nthMondayOf(year, month, n) {
    var candidate = ymdToIso(year, month, 1);
    while (dayOfWeek(candidate) !== 1) candidate = addDays(candidate, 1);
    return addDays(candidate, 7 * (n - 1));
  }

  /** The last Monday of a month, e.g. lastMondayOf(2026, 8) = summer holiday. */
  function lastMondayOf(year, month) {
    // Day 0 of the next month is the last day of this one; Date.UTC handles the
    // December -> January rollover, so month 12 needs no special case.
    var lastDay = new Date(Date.UTC(year, month, 0));
    var candidate = ymdToIso(year, month, lastDay.getUTCDate());
    while (dayOfWeek(candidate) !== 1) candidate = addDays(candidate, -1);
    return candidate;
  }

  /**
   * The eight standard England & Wales bank holidays for a year.
   *
   * New Year, Christmas Day and Boxing Day get a substitute day when they fall
   * on a weekend: the next day that is neither a weekend nor already taken by
   * another holiday. That "already taken" clause is what makes the awkward
   * years come out right — when 25 December is a Sunday, Boxing Day keeps the
   * Monday and Christmas Day is pushed to the Tuesday.
   */
  function computeBankHolidays(year) {
    var holidays = [];

    var addWithSubstitute = function (iso) {
      var candidate = iso;
      while (isWeekend(candidate) || holidays.indexOf(candidate) !== -1) {
        candidate = addDays(candidate, 1);
      }
      holidays.push(candidate);
    };

    addWithSubstitute(ymdToIso(year, 1, 1)); // New Year's Day

    // Good Friday and Easter Monday are defined relative to Easter Sunday and
    // can never land on a weekend, so they never need a substitute.
    var easter = easterSunday(year);
    holidays.push(addDays(easter, -2)); // Good Friday
    holidays.push(addDays(easter, 1)); // Easter Monday

    holidays.push(nthMondayOf(year, 5, 1)); // Early May
    holidays.push(lastMondayOf(year, 5)); // Spring
    holidays.push(lastMondayOf(year, 8)); // Summer

    addWithSubstitute(ymdToIso(year, 12, 25)); // Christmas Day
    addWithSubstitute(ymdToIso(year, 12, 26)); // Boxing Day

    return holidays.sort();
  }

  // Computing eight dates is cheap, but isWorkingDay is called in tight loops
  // while walking the 90-day window, so memoise per year.
  var holidayCache = new Map();

  function holidaysForYear(year) {
    var cached = holidayCache.get(year);
    if (!cached) {
      cached = new Set(computeBankHolidays(year));
      holidayCache.set(year, cached);
    }
    return cached;
  }

  /** The computed England & Wales bank holidays, before any override. */
  function isBankHoliday(isoDate) {
    return holidaysForYear(Number(isoDate.slice(0, 4))).has(isoDate);
  }

  /**
   * Whether JJFO is shut on a date — which makes it both an impossible delivery
   * date AND an impossible packing day.
   *
   * Christmas Day and Boxing Day are closures whatever weekday they land on.
   * That matters because when 25/26 December fall on a weekend it is the
   * SUBSTITUTE Monday/Tuesday that becomes the bank holiday — the actual days
   * are not, and Proovia runs Saturdays while JJFO's own van runs Sundays in the
   * Northwest, so both would otherwise be bookable.
   *
   * `removed` wins over everything else: it is the merchant saying explicitly
   * "we are open that day". Christmas Eve is not a closure by default.
   */
  function isClosureDate(isoDate, blackouts) {
    var b = blackouts || CONFIG.blackouts;
    if (b.removed.has(isoDate)) return false;
    if (b.added.has(isoDate)) return true;
    if (b.christmasDays.has(isoDate.slice(5))) return true;
    return isBankHoliday(isoDate);
  }

  /** Dates that cannot be chosen for delivery. */
  function isBlackoutDate(isoDate, blackouts) {
    return isClosureDate(isoDate, blackouts);
  }

  /** Working day = Monday-Friday, excluding days JJFO is shut. */
  function isWorkingDay(isoDate, blackouts) {
    var dow = dayOfWeek(isoDate);
    var isWeekday = dow >= 1 && dow <= 5;
    return isWeekday && !isClosureDate(isoDate, blackouts);
  }

  // ===========================================================================
  // localRadius.js
  // ===========================================================================

  /**
   * Whether a postcode is within JJFO's 50-mile local-delivery radius, resolved
   * entirely from the bundled district table — no network call.
   *
   * Applies to ANY postcode regardless of region: local is defined by distance
   * alone. Being local shortens JJFO's lead to a same-day pack; it does not
   * change which courier runs on a given day, nor Proovia's own lead.
   *
   * An unparseable postcode is not local — those take the unrecognised-postcode
   * pathway, which resolves before this is ever called.
   */
  function isLocalPostcode(rawPostcode, districts) {
    var set = districts || LOCAL_DISTRICTS;
    var parsed = parsePostcode(rawPostcode);
    if (!parsed) return false;
    return set.has(parsed.outward);
  }

  // ===========================================================================
  // rulesEngine.js
  // ===========================================================================

  /**
   * Small vs large classification: any item that isn't unambiguously "small"
   * makes the whole order follow large-item rules. An item counts as "small"
   * only if it's tagged `small` and NOT also tagged `large`. Untagged or
   * dual-tagged products default to `large` — the safer failure mode, since it
   * forces large-item routing rather than incorrectly offering DPD.
   */
  function classifyCart(cartItems) {
    // An empty cart shouldn't reach here, but `[].every(...)` is vacuously
    // true — guard it explicitly rather than let a degenerate input silently
    // pick the wrong default.
    if (cartItems.length === 0) return 'large';

    var allSmall = cartItems.every(function (item) {
      var tags = (item.tags || []).map(function (tag) {
        return String(tag).toLowerCase();
      });
      return tags.indexOf('small') !== -1 && tags.indexOf('large') === -1;
    });
    return allSmall ? 'small' : 'large';
  }

  /**
   * The "effective order day".
   *
   * Working day, before cutoff -> anchor = today; after cutoff -> the NEXT
   * WORKING DAY. It must be a working-day roll, not a calendar-day one: a
   * calendar roll is swallowed by the weekend on Fridays (both Friday and
   * Saturday have Monday as their next working day), which silently made the
   * cutoff a no-op on every Friday and on Thursdays preceding a bank holiday.
   *
   * Weekend/bank holiday -> the anchor rolls to the next working day too.
   * Rolling the anchor here is what makes a non-local weekend order land on
   * Wednesday: the anchor becomes Monday, and Monday then needs its own full
   * working day to pack.
   *
   * `cutoff === null` (DPD) always anchors on the raw order date — its cutoff is
   * midnight, so the time of day can never move the effective order day.
   */
  function computeAnchorDay(orderDate, cutoff, blackouts) {
    var ukNow = getUkDateTime(orderDate);
    if (cutoff === null) return ukNow.isoDate;
    if (!isWorkingDay(ukNow.isoDate, blackouts)) {
      return nextWorkingDayStrictlyAfter(ukNow.isoDate, blackouts);
    }
    var cutoffMinutes = cutoff.hour * 60 + cutoff.minute;
    return ukNow.minutesSinceMidnight < cutoffMinutes
      ? ukNow.isoDate
      : nextWorkingDayStrictlyAfter(ukNow.isoDate, blackouts);
  }

  /** Pack day for the non-local 1-working-day lead. */
  function nextWorkingDayStrictlyAfter(isoDate, blackouts) {
    var candidate = addDays(isoDate, 1);
    while (!isWorkingDay(candidate, blackouts)) candidate = addDays(candidate, 1);
    return candidate;
  }

  /**
   * Pack day for the local same-day-pack path: the effective order day itself,
   * or the next working day if that day isn't one. Packing only ever happens on
   * a working day, so a weekend or bank-holiday order can't be packed (and
   * therefore can't be delivered) before the following working day.
   */
  function nextWorkingDayOnOrAfter(isoDate, blackouts) {
    var candidate = isoDate;
    while (!isWorkingDay(candidate, blackouts)) candidate = addDays(candidate, 1);
    return candidate;
  }

  function makeDpdCourierForDate(serviceLabels) {
    return function (isoDate) {
      return dayOfWeek(isoDate) === 0
        ? null
        : { courier: 'dpd', serviceLevel: serviceLabels.dpd };
    };
  }

  function makeProoviaOnlyCourierForDate(serviceLabels) {
    return function (isoDate) {
      return dayOfWeek(isoDate) === 0
        ? null
        : { courier: 'proovia', serviceLevel: serviceLabels.proovia };
    };
  }

  /**
   * JJFO on its regional days; Proovia on every other non-Sunday day —
   * including Saturday in the Northwest region.
   */
  function makeRegionCourierForDate(jjfoDays, serviceLabels) {
    return function (isoDate) {
      var dow = dayOfWeek(isoDate);
      if (jjfoDays.indexOf(dow) !== -1) {
        return { courier: 'jjfo', serviceLevel: serviceLabels.jjfo };
      }
      if (dow !== 0) return { courier: 'proovia', serviceLevel: serviceLabels.proovia };
      return null;
    };
  }

  /**
   * Resolves which pathway an order falls into and the earliest date EACH
   * courier on that pathway could deliver.
   *
   * Per-courier rather than a single date because JJFO and Proovia can have
   * different leads for the same order: Proovia collects and packs the item
   * itself, so its 1-working-day lead is its own and JJFO's same-day local
   * packing cannot shorten it. For non-local postcodes the two come out equal.
   */
  function resolvePathway(params) {
    var postcode = params.postcode;
    var cartClass = params.cartClass;
    var orderDate = params.orderDate;
    var config = params.config;

    var blackouts = config.blackouts;
    var cutoffs = config.cutoffs;
    var serviceLabels = config.serviceLabels;

    if (cartClass === 'small') {
      // DPD carries the same 1-full-working-day pack lead as the non-local
      // pathway below. Its midnight cutoff means the anchor is always the order
      // day itself; the pack day is what pushes a Fri/Sat/Sun order out to
      // Tuesday, since nothing is packed over a weekend.
      var dpdAnchor = computeAnchorDay(orderDate, cutoffs.dpd, blackouts);
      var dpdPackDay = nextWorkingDayStrictlyAfter(dpdAnchor, blackouts);
      return {
        earliestByCourier: { dpd: addDays(dpdPackDay, 1) },
        courierForDate: makeDpdCourierForDate(serviceLabels),
      };
    }

    var region = resolveRegion(postcode, config.postcodeAreas);

    // Unrecognised postcode: treated exactly like Proovia-only, so it follows
    // Proovia's cut-off — if the merchant changes when Proovia stops accepting
    // orders, the postcodes Proovia is covering by default move with it.
    if (region === null || region === 'proovia_only') {
      var pAnchor = computeAnchorDay(orderDate, cutoffs.proovia, blackouts);
      var pPackDay = nextWorkingDayStrictlyAfter(pAnchor, blackouts);
      return {
        earliestByCourier: { proovia: addDays(pPackDay, 1) },
        courierForDate: makeProoviaOnlyCourierForDate(serviceLabels),
      };
    }

    var jjfoDays = config.regionDays[region];
    var courierForDate = makeRegionCourierForDate(jjfoDays, serviceLabels);

    // Local is defined by DISTANCE ALONE, with no region qualifier. Distance
    // and region are separate concerns: distance sets the LEAD TIME (below),
    // region sets WHO DELIVERS on each day (`courierForDate`) — so a local
    // postcode outside the Northwest still only gets JJFO on its own region's
    // scheduled days, with Proovia covering the rest. It does NOT get JJFO on
    // demand.
    var local = isLocalPostcode(postcode, config.local.districts);

    // JJFO packs the order itself, so this is the courier the local rule speeds
    // up: packed same-day and delivered next-day via JJFO's own courier, on a
    // 5pm cutoff. Non-local, it takes a full working day to pack, on a noon
    // cutoff.
    var jjfoAnchor = computeAnchorDay(
      orderDate,
      local ? cutoffs.jjfoLocal : cutoffs.jjfoNonLocal,
      blackouts
    );
    var jjfoPackDay = local
      ? nextWorkingDayOnOrAfter(jjfoAnchor, blackouts)
      : nextWorkingDayStrictlyAfter(jjfoAnchor, blackouts);

    // Proovia collects the item from JJFO and packs it ITSELF, so its
    // 1-working-day lead and noon cutoff are its own operation — being local
    // cannot shorten them, because JJFO's packing isn't in Proovia's path at
    // all. For a non-local postcode this lands on the same day as JJFO above,
    // so the distinction only ever changes local orders.
    var prooviaAnchor = computeAnchorDay(orderDate, cutoffs.proovia, blackouts);
    var prooviaPackDay = nextWorkingDayStrictlyAfter(prooviaAnchor, blackouts);

    // No roll-forward past days a courier doesn't run: the caller's loop already
    // skips Sundays and blackouts, so these are plain "not before this date"
    // thresholds. Rolling to the next JJFO day here would strand a local buyer
    // in a one-day-a-week region until that day, which the local rule never
    // promises — the local benefit is the shorter lead time, not a guarantee of
    // JJFO's van.
    return {
      earliestByCourier: {
        jjfo: addDays(jjfoPackDay, 1),
        proovia: addDays(prooviaPackDay, 1),
      },
      courierForDate: courierForDate,
    };
  }

  /**
   * Main entry point: every selectable delivery date from the earliest possible
   * day through the booking window, each with its courier and service-level
   * label.
   *
   * @param {{postcode: string, cartItems: {tags: string[]}[], orderDate?: Date, config?: object}} params
   * @returns {{date: string, courier: 'jjfo'|'proovia'|'dpd', serviceLevel: string}[]}
   */
  function getDeliveryOptions(params) {
    var postcode = params.postcode;
    var cartItems = params.cartItems;
    var orderDate = params.orderDate || new Date();
    var config = params.config || CONFIG;

    var cartClass = classifyCart(cartItems);
    var todayIso = getUkDateTime(orderDate).isoDate;
    var windowEndIso = addDays(todayIso, config.bookingWindowDays);

    var pathway = resolvePathway({
      postcode: postcode,
      cartClass: cartClass,
      orderDate: orderDate,
      config: config,
    });
    var earliestByCourier = pathway.earliestByCourier;
    var courierForDate = pathway.courierForDate;

    // Start at whichever courier is ready soonest; each date is then held to its
    // own courier's threshold below.
    var startDate = Object.keys(earliestByCourier)
      .map(function (key) {
        return earliestByCourier[key];
      })
      .sort()[0];

    var options = [];
    for (var date = startDate; compareIsoDates(date, windowEndIso) <= 0; date = addDays(date, 1)) {
      if (isBlackoutDate(date, config.blackouts)) continue;
      var courierInfo = courierForDate(date);
      if (!courierInfo) continue;
      // The courier that runs this day may not be ready yet, even though another
      // one already is — a local order is packed same-day for JJFO but still
      // waits a working day for Proovia.
      if (compareIsoDates(date, earliestByCourier[courierInfo.courier]) < 0) continue;
      options.push({ date: date, courier: courierInfo.courier, serviceLevel: courierInfo.serviceLevel });
    }
    return options;
  }

  window.JJFODeliveryRules = {
    getDeliveryOptions: getDeliveryOptions,
    classifyCart: classifyCart,
    parsePostcode: parsePostcode,
    resolveRegion: resolveRegion,
    isLocalPostcode: isLocalPostcode,
    addDays: addDays,
    CONFIG: CONFIG,
  };
})();
